/**
 * @file 题目 footer
 */

import {
  faChartLine,
  faEllipsisH,
  faTrashAlt,
  faUserEdit,
} from '@fortawesome/pro-regular-svg-icons'
import { Button, DropdownButton, IconSource, Icon, Modal } from '@seiue/ui'
import styled from 'styled-components'

import { BlockStateEnum } from 'packages/feature-utils/test-paper'

import { BlockFooter, LeftLabel } from '../../BlockFooter'
import { FooterComponent } from '../../BlockWrapper/types'

import { questionTypeEnumToLabel } from './utils'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { getTheme } from '@/utils/theme'

type OperateButtonProps = {
  icon: IconSource
  label: string
  onClick: () => void
  labelColor?: string
  disabled?: boolean
}

/**
 * 块操作按钮
 *
 * @param {OperateButtonProps} param0 - 参数
 * @returns FC
 */
export const OperateButton: React.FC<OperateButtonProps> = ({
  icon,
  label,
  onClick,
  labelColor,
  disabled,
}) => (
  <Button
    size="xs"
    icon={icon}
    type="text"
    onClick={onClick}
    style={{ color: labelColor }}
    disabled={!!disabled}
  >
    {label}
  </Button>
)

/**
 * 问题块的 footer
 *
 * @param {FooterComponent} param0 - 参数
 * @returns FC
 */
export const QuestionBlockFooter: FooterComponent = ({
  blockData,
  onDelete,
  onSolutionClick,
  onChangeState,
  onDuplicate,
  solutionExpanded,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  canEdit,
  key,
  limitLevel = 0,
}) => {
  const theme = getTheme()
  const operates: {
    icon: IconSource
    label: string
    callback: () => void
    labelColor?: string
    disabled?: boolean
  }[] = [
    {
      icon: faChartLine,
      label: !solutionExpanded ? '解析' : '收起解析',
      callback: () => {
        onSolutionClick?.(!solutionExpanded)
      },
      labelColor: solutionExpanded ? theme.brand._1 : theme.text._2,
    },
    ...(canEdit
      ? [
          {
            icon: faUserEdit,
            label: '编辑',
            callback: () => {
              onChangeState(BlockStateEnum.Editing)
            },
          },
          {
            icon: faTrashAlt,
            label: '删除',
            disabled: limitLevel > 3,
            callback: async () => {
              if (limitLevel > 0 && limitLevel < 3) {
                Modal.danger({
                  title: '确定删除该题吗？',
                  content:
                    '已有学生作答此题，删除后将清空学生对该题目的作答情况。确认要继续吗？',
                  okButtonProps: {
                    danger: true,
                  },
                  onOk: async () => {
                    await onDelete?.()
                  },
                })
              } else {
                await onDelete?.()
              }
            },
          },
        ]
      : []),
  ]

  const mainQuestion = blockData.questions?.find((q) => q.parentId === 0)
  return (
    <BlockFooter
      key={key}
      left={
        <LeftLabel>
          {mainQuestion?.type
            ? `${questionTypeEnumToLabel.get(mainQuestion.type)}题`
            : ''}
        </LeftLabel>
      }
      right={
        <Operates>
          {operates.map((item, index) => (
            <OperateButton
              key={index}
              label={item.label}
              icon={item.icon}
              onClick={item.callback}
              labelColor={item.labelColor}
              disabled={item.disabled}
            />
          ))}
          <DropdownButton
            items={[
              ...(limitLevel > 1
                ? []
                : [
                    {
                      label: '复制副本',
                      disabled: !canEdit,
                      render: () => {
                        return (
                          <DropDownItemLabel
                            onClick={() => onDuplicate?.({ range: 'block' })}
                          >
                            <GrowpIcon name="Copy" />
                            复制副本
                          </DropDownItemLabel>
                        )
                      },
                    },
                  ]),
              {
                label: '上移',
                disabled: !canMoveUp,
                render: () => {
                  return (
                    <DropDownItemLabel onClick={() => onMoveUp?.()}>
                      <GrowpIcon
                        name="UpMove"
                        color={canMoveUp ? theme.text._1 : theme.text._4}
                      />
                      上移
                    </DropDownItemLabel>
                  )
                },
              },
              {
                label: '下移',
                disabled: !canMoveDown,
                render: () => {
                  return (
                    <DropDownItemLabel onClick={() => onMoveDown?.()}>
                      <GrowpIcon
                        name="DownMove"
                        color={canMoveDown ? theme.text._1 : theme.text._4}
                      />
                      下移
                    </DropDownItemLabel>
                  )
                },
              },
            ]}
          >
            <Icon
              icon={faEllipsisH}
              color="#8F9197"
              size={16}
              style={{ marginLeft: 12 }}
            />
          </DropdownButton>
        </Operates>
      }
    />
  )
}

const Operates = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const DropDownItemLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`
